import * as Dialog from '@radix-ui/react-dialog';
import MiniSearch from 'minisearch';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Icon, type IconName } from './icons';
import { Button } from './ui';

export interface CommandItem {
  id: string;
  title: string;
  subtitle?: string;
  path: string;
  kind: 'Route' | 'Task' | 'Decision' | 'Module' | 'Claim' | 'Recommendation' | 'Source';
  icon?: IconName;
}

export function CommandPalette({ items }: { items: CommandItem[] }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const mini = useMemo(() => {
    const search = new MiniSearch<CommandItem>({
      fields: ['title', 'subtitle', 'kind'],
      storeFields: ['title', 'subtitle', 'path', 'kind', 'icon'],
    });
    search.addAll(items);
    return search;
  }, [items]);
  const results = query.trim()
    ? mini
        .search(query, { prefix: true, fuzzy: 0.2 })
        .slice(0, 12)
        .map((result) => result as unknown as CommandItem)
    : items.slice(0, 10);
  useEffect(() => setActiveIndex(0), [query, open]);
  const moveSelection = (event: React.KeyboardEvent, next: number) => {
    event.preventDefault();
    const normalized = (next + results.length) % results.length;
    setActiveIndex(normalized);
    const options = document.querySelectorAll<HTMLButtonElement>(
      '.command-palette__results > button',
    );
    options[normalized]?.focus();
  };
  const choose = (path: string) => {
    setOpen(false);
    setQuery('');
    void navigate(path);
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="command-overlay" />
        <Dialog.Content className="command-palette" aria-describedby={undefined}>
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>
          <div className="command-palette__input">
            <Icon name="search" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search routes, tasks, decisions, modules, claims, recommendations and sources…"
              aria-label="Search Akshay’s Atlas"
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' && results.length) moveSelection(event, 0);
                if (event.key === 'ArrowUp' && results.length)
                  moveSelection(event, results.length - 1);
              }}
            />
            <kbd>Esc</kbd>
          </div>
          <div
            className="command-palette__results"
            role="listbox"
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' && results.length)
                moveSelection(event, activeIndex + 1);
              if (event.key === 'ArrowUp' && results.length) moveSelection(event, activeIndex - 1);
            }}
          >
            {results.length ? (
              results.map((item, index) => (
                <button
                  role="option"
                  aria-selected={index === activeIndex}
                  tabIndex={index === activeIndex ? 0 : -1}
                  onFocus={() => setActiveIndex(index)}
                  key={`${item.kind}-${item.id}`}
                  onClick={() => choose(item.path)}
                >
                  <span className="command-result__icon">
                    <Icon name={item.icon ?? 'arrow'} />
                  </span>
                  <span>
                    <strong>{item.title}</strong>
                    <small>{item.subtitle ?? item.kind}</small>
                  </span>
                  <span className="command-result__kind">{item.kind}</span>
                </button>
              ))
            ) : (
              <p className="command-empty">No matching item</p>
            )}
          </div>
          <footer>
            <span>
              <kbd>↑</kbd>
              <kbd>↓</kbd> to navigate
            </span>
            <span>
              <kbd>↵</kbd> to open
            </span>
            <Dialog.Close asChild>
              <Button variant="quiet">Close</Button>
            </Dialog.Close>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
